import React, { PureComponent } from 'react';
import classNames from 'classnames';

import { CheckChunky } from '@eb/eds-iconography';
import { Button } from '@eventbrite/eds-button';
import { Icon } from '@eb/eds-icon';

import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Heading from '../../components/Heading';
import {
    AFTER_DOWNGRADE,
    AFTER_UPDATE,
    CURRENT_PACKAGE,
    FEATURE_FIVE,
    FEATURE_FOUR,
    FEATURE_ONE,
    FEATURE_ONE_V1,
    FEATURE_ONE_V2,
    FEATURE_SIX,
    FEATURE_THREE,
    FEATURE_TWO,
    KEEP_CURRENT,
    TABLE_TITLE_SHORT,
    UPDATE_CTA,
    WARNING,
} from '../../constants/update';
import {
    GA_CHOOSE_PACKAGE,
    GA_PACKAGE_UPDATED,
} from '../../constants/analytics';
import transform from '../transform';
import defaultSettings from './settings';

import './page.scss';
import { isActiveForAU, trackAnalyticsEventAU } from '../utils';

const CheckMark = () => (
    <Icon type={<CheckChunky />} size="small" strokeColor="ui-orange" />
);

const FeatureTableHeaderTitle = ({ title, subTitle }) => {
    let subTitleMarkup = '';

    if (subTitle) {
        subTitleMarkup = (
            <p className="eds-text-bm eds-text-weight--light eds-show-up-mn">
                {subTitle}
            </p>
        );
    }

    return (
        <div className="eds-text--center">
            <p className="eds-text-bl eds-text-weight--heavy eds-show-up-md">
                {title}
            </p>
            <p className="eds-text-bm eds-show-down-mn">{title}</p>
            {subTitleMarkup}
        </div>
    );
};

const FeatureTableRowItem = ({ children }) => (
    <div className="feature-update-table__row-item eds-l-pad-vert-4">
        {children}
    </div>
);

const FeatureTableRow = ({
    isHeader,
    feature,
    previousVersion,
    newVersion,
}) => {
    const classes = classNames('feature-update-table__row', {
        'feature-update-table__header': isHeader,
    });

    return (
        <div className={classes}>
            <FeatureTableRowItem>{feature}</FeatureTableRowItem>
            <FeatureTableRowItem>{previousVersion}</FeatureTableRowItem>
            <FeatureTableRowItem>{newVersion}</FeatureTableRowItem>
        </div>
    );
};

const FeatureTable = () => {
    const featuresTitle = (
        <div>
            <div className="eds-show-down-mn">
                <FeatureTableHeaderTitle title={TABLE_TITLE_SHORT} />
            </div>
        </div>
    );
    const professionalTitle = (
        <FeatureTableHeaderTitle title={CURRENT_PACKAGE} />
    );
    const essentialsTitle = (
        <FeatureTableHeaderTitle
            title={AFTER_UPDATE}
            subTitle={AFTER_DOWNGRADE}
        />
    );

    return (
        <div className="eds-g-group eds-l-pad-vert-14 eds-l-pad-hor-4">
            <div className="eds-g-cell eds-g-cell-1-1">
                <div className="feature-update-table eds-g-cell eds-g-cell-1-1">
                    <FeatureTableRow
                        isHeader={true}
                        feature={featuresTitle}
                        previousVersion={professionalTitle}
                        newVersion={essentialsTitle}
                    />
                    <FeatureTableRow
                        feature={FEATURE_ONE}
                        previousVersion={FEATURE_ONE_V1}
                        newVersion={FEATURE_ONE_V2}
                    />
                    <FeatureTableRow
                        feature={FEATURE_TWO}
                        previousVersion={<CheckMark />}
                        newVersion={<CheckMark />}
                    />
                    <FeatureTableRow
                        feature={FEATURE_THREE}
                        previousVersion={<CheckMark />}
                        newVersion={<CheckMark />}
                    />
                    <FeatureTableRow
                        feature={FEATURE_FOUR}
                        previousVersion={<CheckMark />}
                        newVersion="&mdash;"
                    />
                    <FeatureTableRow
                        feature={FEATURE_FIVE}
                        previousVersion={<CheckMark />}
                        newVersion="&mdash;"
                    />
                    <FeatureTableRow
                        feature={FEATURE_SIX}
                        previousVersion={<CheckMark />}
                        newVersion="&mdash;"
                    />
                </div>
            </div>
        </div>
    );
};

const UpdateCTA = ({ onUpdate, onKeepPackage }) => (
    <div className="eds-g-group eds-l-pad-top-10 eds-l-pad-bot-20 eds-text--center">
        <p className="eds-l-pad-vert-2 eds-text-bm eds-text-weight--heavy">
            {WARNING}
        </p>
        <div className="eds-g-cell eds-g-cell eds-g-cell-1-1 eds-g-cell-sw-1-2 eds-g-cell-md-4-12 eds-g-cell-mw-3-12 eds-l-pad-hor-2 eds-l-pad-vert-2">
            <Button size="block" style="neutral" onClick={onKeepPackage}>
                {KEEP_CURRENT}
            </Button>
        </div>
        <div className="eds-g-cell eds-g-cell eds-g-cell-1-1 eds-g-cell-sw-1-2 eds-g-cell-md-4-12 eds-g-cell-mw-3-12 eds-l-pad-hor-2 eds-l-pad-vert-2">
            <Button size="block" style="fill" onClick={onUpdate}>
                {UPDATE_CTA}
            </Button>
        </div>
    </div>
);

export default class UpdatePage extends PureComponent {
    componentDidMount() {
        this._trackEventActionsAU();
    }

    _trackEventActionsAU() {
        const {
            assortmentsAvailability,
            locale,
            userId,
            userPlan,
        } = this.props;

        const version = assortmentsAvailability
            ? assortmentsAvailability.current.version
            : null;
        if (isActiveForAU(locale)) {
            trackAnalyticsEventAU({
                action: GA_PACKAGE_UPDATED,
                category: GA_CHOOSE_PACKAGE,
                dimensions: {
                    userId,
                    userPlan,
                    version,
                    locale,
                },
            });
        }
    }

    constructor(props) {
        super(props);
        const { breadcrumbs, firstHeading } = transform({ defaultSettings });
        this.breadcrumbs = breadcrumbs;
        this.firstHeading = firstHeading;
    }

    render() {
        const { setPackageOneVersionTwo, onKeepPackage } = this.props;
        return (
            <div>
                <HeaderBreadcrumbs {...this.breadcrumbs} />
                <div className="eds-g-grid marketing-container__max-width--wide">
                    <Heading {...this.firstHeading} />
                    <FeatureTable />
                    <UpdateCTA
                        onKeepPackage={onKeepPackage}
                        onUpdate={setPackageOneVersionTwo}
                    />
                </div>
            </div>
        );
    }
}
